import React, { useEffect } from "react";

const WelcomeLoader = ({ name = "Jabu Zwane", duration = 2200, onDone }) => {
  useEffect(() => {
    if (!onDone) return;
    const t = setTimeout(onDone, duration);
    return () => clearTimeout(t);
  }, [duration, onDone]);

  return (
    <div className="wl-overlay">
      <style>{`
        .wl-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          background:
            radial-gradient(circle at 20% 15%, rgba(193, 122, 86, 0.10) 0%, transparent 45%),
            radial-gradient(circle at 85% 85%, rgba(124, 152, 133, 0.14) 0%, transparent 50%),
            #f6f3ee;
          animation: wlFadeIn 0.4s ease both;
        }

        .wl-overlay--leaving {
          animation: wlFadeOut 0.4s ease both;
        }

        @keyframes wlFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        @keyframes wlFadeOut {
          from { opacity: 1; }
          to   { opacity: 0; }
        }

        .wl-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 28px;
          width: min(320px, 86vw);
          padding: 48px 32px;
          border-radius: 20px;
          background: #fff;
          border: 1px solid rgba(124, 152, 133, 0.18);
          box-shadow: 0 20px 60px -20px rgba(41, 51, 43, 0.18);
          animation: wlRise 0.6s cubic-bezier(0.16, 1, 0.3, 1) both;
        }

        @keyframes wlRise {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .wl-ring-wrap {
          position: relative;
          width: 72px;
          height: 72px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .wl-ring {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          border: 3px solid rgba(124, 152, 133, 0.16);
          border-top-color: var(--ios-blue);
          animation: wlSpin 1s linear infinite;
        }

        .wl-ring--accent {
          inset: 12px;
          border-width: 3px;
          border-color: rgba(193, 122, 86, 0.14);
          border-top-color: var(--ios-blue);
          animation: wlSpin 0.75s linear infinite reverse;
        }

        @keyframes wlSpin {
          to { transform: rotate(360deg); }
        }

        .wl-check {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--ios-blue);
          opacity: 0;
          transform: scale(0.6);
          animation: wlCheckIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) 1.1s forwards;
        }

        @keyframes wlCheckIn {
          to { opacity: 1; transform: scale(1); }
        }

        .wl-text {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 4px;
        }

        .wl-eyebrow {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--ios-blue);
          margin: 0 0 10px;
          opacity: 0;
          animation: wlFadeUp 0.5s ease 0.15s forwards;
        }

        .wl-greeting {
          font-size: 24px;
          font-weight: 600;
          letter-spacing: -0.01em;
          color: #24312a;
          margin: 0;
          line-height: 1.25;
          opacity: 0;
          animation: wlFadeUp 0.5s ease 0.3s forwards;
        }

        .wl-greeting span {
          color: var(--ios-blue);
        }

        .wl-sub {
          font-size: 13.5px;
          color: var(--ios-blue);
          margin: 4px 0 0;
          opacity: 0;
          animation: wlFadeUp 0.5s ease 0.45s forwards;
        }

        @keyframes wlFadeUp {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .wl-dots {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          height: 6px;
          margin-top: 6px;
        }

        .wl-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--ios-blue);
          animation: wlBounce 1.2s ease-in-out infinite;
        }

        .wl-dot:nth-child(2) { animation-delay: 0.15s; }
        .wl-dot:nth-child(3) { animation-delay: 0.3s; }

        @keyframes wlBounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.35; }
          40% { transform: translateY(-4px); opacity: 1; }
        }

        @media (prefers-reduced-motion: reduce) {
          .wl-overlay, .wl-card, .wl-ring, .wl-ring--accent, .wl-check,
          .wl-eyebrow, .wl-greeting, .wl-sub, .wl-dot {
            animation: none !important;
            opacity: 1;
            transform: none !important;
          }
        }
      `}</style>

      <div className="wl-card">
        <div className="wl-ring-wrap">
          <div className="wl-ring" />
          <div className="wl-ring wl-ring--accent" />
          <div className="wl-check">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
              <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>

        <div className="wl-text">
          <p className="wl-eyebrow">Signing you in</p>
          <h1 className="wl-greeting">
            Welcome, <span>{name}</span>
          </h1>
          <p className="wl-sub">Getting your dashboard ready</p>
          <div className="wl-dots">
            <span className="wl-dot" />
            <span className="wl-dot" />
            <span className="wl-dot" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeLoader;